import { type AxiosError } from "axios";
import { format } from "date-fns";
import moment from "moment";

import { BTSelectItem, type ExceptionResponse } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";

import {
    DraftItemChangeType,
    ModifyCalendarEnum,
    type ScheduleDraftChange,
    type ScheduleDraftScheduleItem,
    scheduleDraftsPostV2,
    scheduleDraftsPreviewPostV2,
    type SchedulePredecessor,
} from "handlers";

import {
    type GanttEntity,
    type GanttEntry,
    GanttItemType,
    GanttLink,
    type GanttPredecessor,
} from "entity/schedule/Gantt/Gantt.api.types";
import { Predecessor, type ScheduleEntity } from "entity/schedule/Schedule/Schedule.api.types";
import {
    DraftOperations,
    type ICombinedHistoryItem,
    type IDraftHistoryState,
    type IScheduleDraftRevisionState,
} from "entity/schedule/ScheduleDrafts/ScheduleDrafts.types";
import { type IInlineEditRequest } from "entity/schedule/ScheduleList/ScheduleList.api.types";

// ========================================
// BASIC DRAFT OPERATIONS
// ========================================

export function buildDraftLinkBreakItem(
    link: GanttLink,
    draftState: IScheduleDraftRevisionState | undefined,
    listEntity: GanttEntity,
): ScheduleDraftScheduleItem {
    const predecessors = getTaskPredecessors(link.target, draftState, listEntity).filter(
        (p) => p.predecessorId !== link.source,
    );

    return {
        changeType: DraftItemChangeType.RemoveLink,
        id: link.target,
        predecessors,
    };
}

export function buildDraftLinkCreateItem(
    link: GanttLink,
    draftState: IScheduleDraftRevisionState | undefined,
    listEntity: GanttEntity,
): ScheduleDraftScheduleItem {
    const predecessors = [
        ...getTaskPredecessors(link.target, draftState, listEntity),
        { predecessorId: link.source, type: link.type },
    ];

    return {
        changeType: DraftItemChangeType.CreateLink,
        id: link.target,
        predecessors,
    };
}

export function buildDraftEditItem(req: IInlineEditRequest): ScheduleDraftScheduleItem {
    return {
        changeType: DraftItemChangeType.Modify,
        id: req.id,
        startDate: req.startDate?.toDate(),
        endDate: req.endDate?.toDate(),
        title: req.title,
        duration: req.duration,
    };
}

export async function saveDraft(draftState: IScheduleDraftRevisionState) {
    try {
        return await scheduleDraftsPostV2(draftState);
    } catch (error) {
        showApixErrorMessage(error as AxiosError<ExceptionResponse>);
        throw error;
    }
}

export async function applyDraftChange(
    listEntity: GanttEntity | undefined,
    draftState: IScheduleDraftRevisionState,
    change: ScheduleDraftScheduleItem,
): Promise<{
    newListEntity: GanttEntity;
    newDraftState: IScheduleDraftRevisionState;
}> {
    if (!listEntity) {
        throw new Error("Cannot apply draft change: listEntity is undefined.");
    }

    let newDraftState = draftState;

    if (change.changeType === DraftItemChangeType.Modify) {
        try {
            const response = await scheduleDraftsPreviewPostV2({
                draftState,
                change,
            });
            const delta =
                typeof change.id === "number"
                    ? generateDelta(draftState.currentState, response.data.currentState, change.id)
                    : {};

            newDraftState = { ...response.data, delta };
        } catch (error) {
            showApixErrorMessage(error as AxiosError<ExceptionResponse>);
            throw error;
        }
    } else if (
        change.changeType === DraftItemChangeType.CreateLink ||
        change.changeType === DraftItemChangeType.RemoveLink
    ) {
        newDraftState = updatePreds(change, draftState);
    }

    newDraftState = {
        ...newDraftState,
        sourceItemId: change.id ?? null,
    };

    const newListEntity = updateScheduleWithDraftChanges(listEntity, newDraftState);
    return { newListEntity, newDraftState };
}

function generateDelta(
    previousState: ScheduleDraftChange,
    newState: ScheduleDraftChange,
    scheduleItemId: number,
) {
    const previous = previousState.items.find((item) => item.id === scheduleItemId);
    const current = newState.items.find((item) => item.id === scheduleItemId);

    return {
        actionTaken: ModifyCalendarEnum.Schedule_Item,
        sourceScheduleShift: {
            scheduleItemId: scheduleItemId ?? undefined,
            scheduleItemName: current?.title,
            previousStartDate: previous?.startDate ?? undefined,
            previousEndDate: previous?.endDate ?? undefined,
            newStartDate: current?.startDate ?? undefined,
            newEndDate: current?.endDate ?? undefined,
            scheduleColor: current?.color,
            baselineStart: null,
            baselineEnd: null,
            workdaySlip: 0,
            scheduleItemDuration: current?.duration ?? undefined,
        },
        isDraft: true,
        shifts: [],
        actionDate: new Date(),
    } as ICombinedHistoryItem;
}

export function updatePreds(
    change: ScheduleDraftScheduleItem,
    draftState: IScheduleDraftRevisionState,
): IScheduleDraftRevisionState {
    const updatedPreds =
        change.predecessors?.map((pred) => ({
            predecessorId: pred.predecessorId,
            type: pred.type,
            linkVisible: true,
        })) ?? [];
    const existingItem = draftState.currentState.items.find((item) => item.id === change.id);
    const updatedItem: ScheduleDraftScheduleItem = {
        ...existingItem!,
        predecessors: updatedPreds,
    };
    return {
        ...draftState,
        currentState: {
            ...draftState.currentState,
            items: draftState.currentState.items.map((item) =>
                item.id === change.id ? updatedItem : item,
            ),
        },
        delta: undefined,
    };
}

export function updateScheduleWithDraftChanges(
    listEntity: GanttEntity,
    draftState: IScheduleDraftRevisionState,
): GanttEntity {
    const draftSchedules = draftState.currentState.items;

    const updatedScheduleItems = listEntity.taskData.data.map((li: GanttEntry) => {
        const updatedSchedule = draftSchedules.find((x) => x.id === li.id);
        if (!updatedSchedule) return { ...li };

        const updatedPreds =
            updatedSchedule.predecessors
                ?.filter((pred) => pred.type !== undefined)
                .map((pred) => ({
                    predecessorId: pred.predecessorId!,
                    scheduleId: updatedSchedule.id!,
                    predecessorType: pred.type!,
                    linkVisible: true,
                    lag: pred.lag ?? 0,
                })) ?? [];

        return {
            ...li,
            startDate: moment(updatedSchedule.startDate),
            endDate: moment(updatedSchedule.endDate),
            duration: updatedSchedule.duration ?? 0,
            enteredDuration: updatedSchedule.duration ?? 0,
            start_date: moment(updatedSchedule.startDate).startOf("day").toDate(),
            end_date: moment(updatedSchedule.endDate).endOf("day").toDate(),
            actualStart: moment(updatedSchedule.startDate).toDate(),
            actualEnd: moment(updatedSchedule.endDate).toDate(),
            predecessors: updatedPreds,
        };
    });

    return rebuildEntityWithUpdatedItems(listEntity, updatedScheduleItems);
}

export function rebuildEntityWithUpdatedItems(
    liveEntity: GanttEntity,
    updatedItems: GanttEntry[],
): GanttEntity {
    const newStartDate = updatedItems
        .reduce((minDate, item) => {
            const start = moment(item.startDate);
            return start.isBefore(minDate) ? start : minDate;
        }, moment(liveEntity.startDate))
        .toDate();

    const newEndDate = updatedItems
        .reduce((maxDate, item) => {
            const end = moment(item.endDate);
            return end.isAfter(maxDate) ? end : maxDate;
        }, moment(liveEntity.endDate))
        .toDate();

    const allPredecessors = updatedItems.flatMap((item) =>
        (item.predecessors ?? []).map((p) => ({ ...p, scheduleId: item.id })),
    );

    let linkId = 0;
    const links = allPredecessors.map(
        (p) =>
            new GanttLink({
                id: linkId++,
                source: p.predecessorId,
                target: p.scheduleId,
                type: p.predecessorType,
                lag: p.lag,
            }),
    );

    return {
        ...liveEntity,
        startDate: newStartDate,
        endDate: newEndDate,
        taskData: {
            ...liveEntity.taskData,
            lastUpdatedDate: new Date(),
            data: updatedItems,
            links: links,
        },
    };
}

export function getTaskPredecessors(
    taskId: number,
    draftState: IScheduleDraftRevisionState | undefined,
    listEntity: GanttEntity | undefined,
): SchedulePredecessor[] {
    if (draftState) {
        return draftState.currentState.items.find((i) => i.id === taskId)?.predecessors ?? [];
    }

    if (listEntity) {
        const ganttPredecessors =
            listEntity.taskData.data.find((i) => i.id === taskId)?.predecessors ?? [];
        return ganttPredecessors.map((p: GanttPredecessor) => ({
            predecessorId: p.predecessorId,
            type: p.predecessorType,
            lag: p.lag,
        }));
    }

    return [];
}

export function generateScheduleDraftItemsFromGanttEntity(
    entity: GanttEntity,
): ScheduleDraftScheduleItem[] {
    return entity.taskData.data
        .filter((entry) => entry.itemType === GanttItemType.Schedule)
        .map((entry) => {
            const predecessors: SchedulePredecessor[] | null =
                entry.predecessors.length > 0
                    ? entry.predecessors.map((p) => ({
                          predecessorId: p.predecessorId,
                          type: p.predecessorType,
                          lag: p.lag,
                      }))
                    : null;

            return {
                id: entry.id,
                changeType: DraftItemChangeType.Modify,
                title: entry.text,
                startDate: entry.startDate.toDate(),
                endDate: entry.endDate.toDate(),
                markedCompleteDate: entry.completedDate?.toDate() ?? null,
                isHourly: entry.hasHourlyStart || entry.hasHourlyEnd,
                color: entry.color,
                duration: entry.duration,
                assignees: entry.selectedAssignees ?? null,
                phase: entry.phase?.id ?? null,
                predecessors,
            };
        });
}

export function showDiscardDraftDialog(onEndDraftMode: () => void) {
    btConfirm({
        title: "Discard changes",
        content: "Do you want to discard your unsaved changes?",
        okText: "Discard",
        cancelText: "Keep editing",
        onOk: async () => {
            onEndDraftMode();
        },
        okType: "danger",
    });
}

// ========================================
// SNAPSHOT MANAGEMENT
// ========================================

export function createOriginalScheduleSnapshot(entity: GanttEntity): ScheduleDraftScheduleItem[] {
    if (!entity?.taskData?.data) {
        return [];
    }

    return entity.taskData.data
        .filter((entry) => entry.itemType === GanttItemType.Schedule)
        .map((entry) => ({
            id: entry.id,
            changeType: DraftItemChangeType.Modify,
            title: entry.text,
            startDate: entry.startDate.toDate(),
            endDate: entry.endDate.toDate(),
            duration: entry.duration,
            predecessors:
                entry.predecessors?.length > 0
                    ? entry.predecessors.map((p) => ({
                          predecessorId: p.predecessorId,
                          type: p.predecessorType,
                          lag: p.lag,
                      }))
                    : null,
            markedCompleteDate: entry.completedDate?.toDate() ?? null,
            isHourly: entry.hasHourlyStart || entry.hasHourlyEnd,
            color: entry.color,
            assignees: entry.selectedAssignees ?? null,
            phase: entry.phase?.id ?? null,
        }));
}

export function isJobDraftChange(
    prev: IDraftHistoryState | null,
    curr: IDraftHistoryState | null,
): boolean {
    const prevHistory = prev?.history.length ?? 0;
    const currHistory = curr?.history.length ?? 0;
    const prevFuture = prev?.future.length ?? 0;
    const currFuture = curr?.future.length ?? 0;

    return (
        // 1. No history → history (initial draft change)
        (prevHistory === 0 && currHistory > 0) ||
        // 2. Had history or future → now both are empty (cleared all changes)
        ((prevHistory > 0 || prevFuture > 0) && currHistory === 0 && currFuture === 0)
    );
}

export function applyDraftChangeToScheduleEntity(
    items: ScheduleDraftScheduleItem[],
    entity: ScheduleEntity,
) {
    const item = items.find((i) => i.id === entity.id);

    if (!item) {
        return entity;
    }

    if (item.title !== undefined && item.title !== null) {
        entity.title = item.title;
    }
    if (item.startDate !== undefined) {
        entity.startDate = item.startDate ? moment(item.startDate) : undefined;
    }
    if (item.endDate !== undefined) {
        entity.endDate = item.endDate ? moment(item.endDate) : undefined;
    }
    if (item.color) {
        entity.color = item.color;
    }
    if (item.duration) {
        entity.duration = item.duration;
    }
    if (item.predecessors) {
        entity.predecessors = item.predecessors.map(
            (p) =>
                new Predecessor({
                    scheduleItem: {
                        validators: [{ errorMessage: null, type: "required" }],
                        value: [
                            {
                                key: null,
                                id: p.predecessorId ?? 0,
                                name: "",
                                secondaryName: null,
                                selected: true,
                                extraData: null,
                                disabled: false,
                            },
                        ],
                        unselectedId: -1,
                        title: null,
                    },
                    lag: {
                        value: p.lag ?? 0,
                        validators: [{ errorMessage: null, type: "required" }],
                    },
                    linkedType: {
                        validators: [{ errorMessage: null, type: "required" }],
                        unselectedId: -1,
                        title: null,
                        value: [
                            {
                                key: null,
                                id: 1,
                                name: "Finish-to-Start (FS)",
                                secondaryName: null,
                                selected: p.type === 1,
                                extraData: null,
                                disabled: false,
                            },
                            {
                                key: null,
                                id: 2,
                                name: "Start-to-Start (SS)",
                                secondaryName: null,
                                selected: p.type === 2,
                                extraData: null,
                                disabled: false,
                            },
                        ],
                    },
                    dateTimeRange: "",
                    isCompleted: false,
                    progress: "",
                    id: p.predecessorId ?? 0,
                    title: "",
                    numLagDays: p.lag ?? 0,
                }),
        );

        entity.predecessorList = items
            .filter((p) => p.id !== entity.id)
            .map(
                (p) =>
                    new BTSelectItem({
                        key: p.id,
                        id: p.id,
                        name: `${p.title} - ${format(
                            new Date(p.startDate!),
                            "M-d-yyyy",
                        )} to ${format(new Date(p.endDate!), "M-d-yyyy")}`,
                        value: p.id,
                        secondaryName: null,
                        selected: false,
                        disabled: false,
                    }),
            );
    }
    return entity;
}

// ========================================
// DRAFT MODE HELPER FUNCTIONS
// ========================================

/**
 * Determines if draft mode should be enabled based on localStorage setting and draft flag
 */
export function shouldUseDraftMode(draftModeFlag: boolean): boolean {
    return (BTLocalStorage.get("bt-boolean-useDraftMode") ?? false) && draftModeFlag;
}

/**
 * Helper function to determine if draft mode is active based on showDraftMode state or operation
 */
export function isDraftModeActive(
    showDraftMode: boolean,
    draftOperation?: DraftOperations,
): boolean {
    return showDraftMode || draftOperation === DraftOperations.EnableDraftMode;
}
